<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import { useApiClient } from "@/api/client";
import { colorForFactor } from "@/modules/booking_experts/utils";
import { formatNumber } from "chartjs-graph";
import dayjs from "dayjs";

const headers = [
	{
		title: "Booking Reference",
		align: "start",
		key: "lodging_booking",
		sortable: false,
	},
	{
		title: "Revenue (Ex VAT)",
		align: "start",
		key: "revenue_ex_vat",
		sortable: false,
	},
	{
		title: "Pricing Factor Applied",
		align: "start",
		key: "pricing_factor_applied",
		sortable: false,
	},
	{
		title: "Arrival Date",
		align: "start",
		key: "period_subject_date_time_fr",
		sortable: false,
	},
	{
		title: "Departure Date",
		align: "start",
		key: "period_subject_date_time_to",
		sortable: false,
	},
	{
		title: "Status",
		align: "start",
		key: "lodging_status",
		sortable: false,
	},
];

const isLoading = ref(true);
const items = ref();

onBeforeMount(() => fetchRecentBookings());

const fetchRecentBookings = async () => {
	isLoading.value = true;
	const response = await useApiClient("bookingExperts").getBookings({
		page: 1,
		per_page: 4,
	});
	if (response.isOk()) {
		items.value = response.value.data;
	}
	isLoading.value = false;
};
</script>

<template>
	<v-card class="home-graph-card">
		<v-card-title class="bg-background" style="height: 48px">
			Recent bookings
		</v-card-title>

		<div class="d-flex flex-column justify-space-between">
			<v-card-text class="px-2 py-0">
				<v-data-table
					density="compact"
					hide-default-header
					hide-default-footer
					:items="items"
					:headers="headers"
					:loading="isLoading"
					items-per-page="4"
				>
					<template #no-data>
						<div class="my-3">There are currently no bookings</div>
					</template>
					<template v-slot:item.lodging_booking="{ item }">
						<div class="d-flex align-items-center">
							<div
								class="py-1"
								style="
									text-wrap: nowrap;
									max-width: 250px;
									overflow: hidden;
									text-overflow: ellipsis;
								"
							>
								{{ item.lodging_booking }}
							</div>
						</div>
					</template>
					<template v-slot:item.revenue_ex_vat="{ item }">
						{{ formatNumber(item.revenue_ex_vat, "money") }}
					</template>
					<template v-slot:item.period_subject_date_time_fr="{ item }">
						{{ dayjs(item.period_subject_date_time_fr).format("D MMM") }}
					</template>
					<template v-slot:item.period_subject_date_time_to="{ item }">
						{{ dayjs(item.period_subject_date_time_to).format("D MMM") }}
					</template>
					<template v-slot:item.pricing_factor_applied="{ item }">
						<v-chip
							v-if="item.pricing_factor_applied"
							density="compact"
							label
							:color="colorForFactor(item.pricing_factor_applied)"
						>
							{{ item.pricing_factor_applied.toFixed(2) }}
						</v-chip>
						<v-chip v-else density="compact" disabled label>-</v-chip>
					</template>
				</v-data-table>
				<v-divider opacity="0.25" />
			</v-card-text>

			<div class="d-flex pa-2">
				<v-spacer />
				<v-chip variant="flat" to="/pricing/bookings">
					Go to booking overview
				</v-chip>
			</div>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.home-graph-card {
	display: grid;
	grid-template-rows: auto 1fr;
}
</style>
